import {BaseCopilotChatButton, squareHeightSx} from './BaseCopilotChatButton'
import {publishAddCopilotChatReference, publishOpenCopilotChat} from '@github-ui/copilot-chat/utils/copilot-chat-events'
import {CopilotChatIntents, type FileDiffReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {matchHash} from '@github-ui/diff-lines/document-hash-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {IconButtonWithTooltip} from '@github-ui/icon-button-with-tooltip'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useToastContext} from '@github-ui/toast/ToastContext'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {subscribeDiffSelection, type DiffSelectionEvent} from './diff-selection'
import {populateSnippetRanges} from './populate-snippet-ranges'

const containerSx = {position: 'absolute', top: '-12px', right: '12px', zIndex: 1} as const

export interface CopilotChatDiffButtonProps {
  fileDiffReference: FileDiffReference
  style?: React.CSSProperties
}

export const CopilotChatDiffButton = ({fileDiffReference}: CopilotChatDiffButtonProps) => {
  const copilot_diff_chat_icebreakers = isFeatureEnabled('COPILOT_DIFF_CHAT_ICEBREAKERS')

  const [portalElement, setPortalElement] = useState<Element>()
  const [diffSelection, setDiffSelection] = useState<DiffSelectionEvent['payload']>()

  const [open, setOpen] = useState(false)
  const {addToast} = useToastContext()

  useEffect(() => {
    const handleDiffSelection = (e: Pick<DiffSelectionEvent, 'payload'>) => {
      setDiffSelection(e.payload)

      // get the topmost and furthest-right selected line, and use that as the portal element
      const elements = document.querySelectorAll('.selected-line.selected-line-top.blob-code')
      const element = elements[elements.length - 1]

      if (element && fileDiffReference.id && e.payload?.diffId && fileDiffReference.id === e.payload.diffId) {
        setPortalElement(element)
      } else {
        setPortalElement(undefined)
      }
    }

    // parse hash on first render
    if (ssrSafeWindow) {
      const matches = matchHash(ssrSafeWindow.location.hash)
      if (matches?.[1]) {
        handleDiffSelection({
          payload: {
            diffId: matches[1],
            start: matches[2] && matches[3] ? `${matches[2]}${matches[3]}` : undefined,
            end: matches[4] && matches[5] ? `${matches[4]}${matches[5]}` : undefined,
          },
        })
      }
    }

    return subscribeDiffSelection(handleDiffSelection)
  }, [fileDiffReference])

  // user-selected line ranges sometimes need to be added to the reference
  const populateRangesAndSelection = () => ({
    ...populateSnippetRanges(fileDiffReference),
    selectedRange: {start: diffSelection?.start ?? '', end: diffSelection?.end ?? ''},
  })

  const handleDiscuss = () => {
    publishOpenCopilotChat({
      intent: CopilotChatIntents.discussFileDiff,
      references: [populateRangesAndSelection()],
    })
    setOpen(false)
    sendEvent('copilot.file-diff.discuss')
  }

  const handleExplain = () => {
    publishOpenCopilotChat({
      content: 'Explain',
      intent: CopilotChatIntents.explainFileDiff,
      references: [populateRangesAndSelection()],
    })
    setOpen(false)
    sendEvent('copilot.file-diff.explain')
  }

  const handleAddReferenceCallback = () => {
    publishAddCopilotChatReference(populateRangesAndSelection())
    addToast({message: 'Reference added to thread', type: 'success'})
    setOpen(false)
    sendEvent('copilot.file-diff.add')
  }

  return (
    <>
      {portalElement
        ? createPortal(
            <BaseCopilotChatButton
              referenceType={fileDiffReference.type}
              onClick={handleDiscuss}
              containerSx={containerSx}
            >
              <ActionMenu open={open} onOpenChange={setOpen}>
                <ActionMenu.Anchor>
                  <IconButtonWithTooltip
                    icon={TriangleDownIcon}
                    label="Copilot menu"
                    onSelect={() => setOpen(true)}
                    size="small"
                    sx={{...squareHeightSx, '>span': {mb: 0}, color: 'fg.muted'}}
                    tooltipDirection="sw"
                    data-testid="more-copilot-button"
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay align="end">
                  <ActionList>
                    {copilot_diff_chat_icebreakers && (
                      <>
                        <ActionList.Item onSelect={handleExplain}>Explain</ActionList.Item>
                        <ActionList.Divider />
                      </>
                    )}
                    <ActionList.Item onSelect={handleAddReferenceCallback}>Attach to current thread</ActionList.Item>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </BaseCopilotChatButton>,
            portalElement,
          )
        : null}
    </>
  )
}

try{ CopilotChatDiffButton.displayName ||= 'CopilotChatDiffButton' } catch {}